import { useEffect, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";
import "./WalletActivityBadgesPage.css";

type SourceItem = {
  id:string;
  title:string;
  subtitle?:string|null;
  status:string;
  occurredAt?:string|null;
  verifiedAt?:string|null;
};

type SourceCatalog = {
  schoolExperiences:SourceItem[];
  personalExperiences:SourceItem[];
  evidence:SourceItem[];
};

type ActivityBadge = {
  badgeId:string;
  title:string;
  description?:string|null;
  activityType:string;
  activityName:string;
  activityOn?:string|null;
  iconKey:string;
  themeKey:string;
  selfIssuedAt:string;
  standardLabel:string;
  certificationStatus:string;
  sourceCount:number;
  verifiedEvidenceCount:number;
  certificationRequest?:{
    requestId:string;
    status:string;
    requestNote?:string|null;
    requestedAt:string;
    decisionReason?:string|null;
  }|null;
  officialCredential?:{
    credentialId:string;
    credentialName:string;
    externalCredentialId:string;
    verificationUri?:string|null;
    status:string;
    issuedAt:string;
  }|null;
};

type BadgeData = {badges:ActivityBadge[]};

const icons = ["✦","★","◆","●","▲","☀","✓","◎"];
const themes = ["NAVY","SKY","MINT","SUN","LILAC","CORAL","GRAPHITE"];

function formatDate(value?:string|null){
  if(!value)return "-";
  const d=new Date(value);
  if(Number.isNaN(d.getTime()))return value;
  return d.toLocaleDateString("ko-KR",{year:"numeric",month:"2-digit",day:"2-digit"});
}

function certificationLabel(status:string){
  const map:Record<string,string>={
    SELF_ISSUED:"자가발급",
    REQUESTED:"본사 인증 심사 중",
    REJECTED:"인증 보완 필요",
    CANCELLED:"인증 요청 취소",
    APPROVED_ISSUANCE_PENDING:"본사 승인 · 공식 발급 처리 중",
    CERTIFIED:"공식 Life Stamp 승격"
  };
  return map[status] || status;
}

export function WalletActivityBadgesPage({
  client,
  onNavigate
}:{
  client:PlatformClient;
  onNavigate:(path:string)=>void;
}){
  const [catalog,setCatalog]=useState<SourceCatalog|null>(null);
  const [data,setData]=useState<BadgeData|null>(null);
  const [busy,setBusy]=useState("");
  const [error,setError]=useState("");
  const [notice,setNotice]=useState("");

  const [title,setTitle]=useState("");
  const [activityName,setActivityName]=useState("");
  const [activityType,setActivityType]=useState("나의 활동");
  const [activityOn,setActivityOn]=useState("");
  const [description,setDescription]=useState("");
  const [iconKey,setIconKey]=useState("✦");
  const [themeKey,setThemeKey]=useState("NAVY");
  const [selected,setSelected]=useState<Record<string,boolean>>({});
  const [requestNotes,setRequestNotes]=useState<Record<string,string>>({});

  async function load(){
    const [sourceResult,badgeResult]=await Promise.all([
      client.rpc("wallet_activity_badge_source_catalog"),
      client.rpc("wallet_activity_badges")
    ]);
    if(sourceResult.error){setError(sourceResult.error.message);return;}
    if(badgeResult.error){setError(badgeResult.error.message);return;}
    setCatalog(sourceResult.data as SourceCatalog);
    setData(badgeResult.data as BadgeData);
    setError("");
  }

  useEffect(()=>{void load();},[client]);

  function toggle(key:string){
    setSelected((current)=>({...current,[key]:!current[key]}));
  }

  function selectedIds(prefix:string,items:SourceItem[]){
    return items.filter((item)=>selected[prefix+item.id]).map((item)=>item.id);
  }

  async function createBadge(){
    if(!catalog||!title.trim()||!activityName.trim()||!activityType.trim())return;
    setBusy("create");setError("");setNotice("");
    const {error}=await client.rpc("wallet_create_activity_badge",{
      p_title:title.trim(),
      p_description:description.trim()||null,
      p_activity_type:activityType.trim(),
      p_activity_name:activityName.trim(),
      p_activity_on:activityOn||null,
      p_icon_key:iconKey,
      p_theme_key:themeKey,
      p_participation_ids:selectedIds("P:",catalog.schoolExperiences),
      p_personal_experience_ids:selectedIds("X:",catalog.personalExperiences),
      p_evidence_ids:selectedIds("E:",catalog.evidence)
    });
    if(error){setError(error.message);setBusy("");return;}
    setTitle("");setActivityName("");setActivityType("나의 활동");setActivityOn("");setDescription("");
    setSelected({});
    setNotice("학생 활동배지를 자가발급했습니다.");
    await load();
    setBusy("");
  }

  async function requestCertification(badge:ActivityBadge){
    setBusy("request:"+badge.badgeId);setError("");setNotice("");
    const {error}=await client.rpc("wallet_request_activity_badge_certification",{
      p_badge_id:badge.badgeId,
      p_note:(requestNotes[badge.badgeId]||"").trim()||null
    });
    if(error){setError(error.message);setBusy("");return;}
    setNotice("본사 인증을 요청했습니다.");
    await load();
    setBusy("");
  }

  async function cancelCertification(requestId:string){
    setBusy("cancel:"+requestId);setError("");setNotice("");
    const {error}=await client.rpc("wallet_cancel_activity_badge_certification",{p_request_id:requestId});
    if(error){setError(error.message);setBusy("");return;}
    setNotice("인증 요청을 취소했습니다.");
    await load();
    setBusy("");
  }

  if(!catalog||!data)return <p className="muted">활동배지 스튜디오를 준비하는 중…</p>;

  return (
    <section className="wallet-page activity-badge-page">
      <div className="activity-badge-hero">
        <div>
          <p className="eyebrow">STUDENT ACTIVITY BADGE</p>
          <h2>내 활동에 내가 배지를 만들어요</h2>
          <p>학생이 자유롭게 만들고 자가발급하는 활동 기록입니다. 본사 인증 전에는 국제표준 자격증명이 아닙니다.</p>
        </div>
        <div className="activity-badge-tier">
          <strong>학생 활동배지 → 본사 인증 → 공식 Life Stamp</strong>
          <span>본사 인증은 기존 Open Badges 3.0 발급 기준과 검증 절차를 그대로 사용합니다.</span>
        </div>
      </div>

      {error&&<p className="error">{error}</p>}
      {notice&&<p className="activity-badge-notice">{notice}</p>}

      <div className="activity-badge-layout">
        <section className="activity-badge-create">
          <div className="section-heading">
            <div><p className="eyebrow">CREATE</p><h3>새 활동배지</h3></div>
            <span className="phase-badge">비표준 · 자가발급</span>
          </div>

          <div className="activity-badge-form">
            <label>배지 이름<input value={title} maxLength={120} onChange={(e)=>setTitle(e.target.value)} placeholder="예: 첫 직업인 인터뷰 완료" /></label>
            <label>활동 이름<input value={activityName} maxLength={160} onChange={(e)=>setActivityName(e.target.value)} placeholder="예: UX 디자이너 인터뷰" /></label>
            <label>활동 종류<input value={activityType} maxLength={80} onChange={(e)=>setActivityType(e.target.value)} placeholder="예: 인터뷰, 프로젝트, 독서" /></label>
            <label>활동일<input type="date" value={activityOn} onChange={(e)=>setActivityOn(e.target.value)} /></label>
            <label>설명<textarea value={description} maxLength={1600} onChange={(e)=>setDescription(e.target.value)} placeholder="무엇을 했고 왜 의미 있었는지 적어보세요." /></label>

            <div className="activity-badge-picker">
              <strong>아이콘</strong>
              <div>{icons.map((icon)=><button type="button" key={icon} className={iconKey===icon?"active":""} onClick={()=>setIconKey(icon)}>{icon}</button>)}</div>
            </div>

            <div className="activity-badge-picker">
              <strong>테마</strong>
              <div>{themes.map((theme)=><button type="button" key={theme} className={themeKey===theme?"active":""} onClick={()=>setThemeKey(theme)}>{theme}</button>)}</div>
            </div>

            <SourceSelector title="학교 활동" prefix="P:" items={catalog.schoolExperiences} selected={selected} onToggle={toggle} />
            <SourceSelector title="나의 경험" prefix="X:" items={catalog.personalExperiences} selected={selected} onToggle={toggle} />
            <SourceSelector title="증빙" prefix="E:" items={catalog.evidence} selected={selected} onToggle={toggle} />

            <button className="primary" disabled={busy!==""||!title.trim()||!activityName.trim()||!activityType.trim()} onClick={createBadge}>
              {busy==="create"?"발급 중…":"학생 활동배지 자가발급"}
            </button>
          </div>
        </section>

        <section className="activity-badge-vault">
          <div className="section-heading">
            <div><p className="eyebrow">MY BADGES</p><h3>내 활동배지</h3></div>
            <span>{data.badges.length}개</span>
          </div>

          <div className="activity-badge-list">
            {data.badges.map((badge)=>(
              <article className={"activity-badge-card theme-"+badge.themeKey.toLowerCase()} key={badge.badgeId}>
                <div className="activity-badge-art">
                  <span>{badge.iconKey}</span>
                  <strong>{badge.title}</strong>
                  <small>{badge.standardLabel}</small>
                </div>

                <div className="activity-badge-body">
                  <div className="activity-badge-state">
                    <span>{certificationLabel(badge.certificationStatus)}</span>
                    <small>{formatDate(badge.selfIssuedAt)}</small>
                  </div>
                  <h4>{badge.activityName}</h4>
                  <p>{badge.activityType+(badge.activityOn?" · "+formatDate(badge.activityOn):"")}</p>
                  {badge.description&&<p>{badge.description}</p>}
                  <div className="activity-badge-facts">
                    <span>연결 기록 {badge.sourceCount}</span>
                    <span>검증 증빙 {badge.verifiedEvidenceCount}</span>
                  </div>

                  {badge.certificationStatus==="REJECTED"&&badge.certificationRequest?.decisionReason&&(
                    <div className="activity-badge-feedback">
                      <strong>본사 심사 의견</strong>
                      <p>{badge.certificationRequest.decisionReason}</p>
                    </div>
                  )}

                  {badge.officialCredential ? (
                    <div className="activity-badge-official">
                      <strong>{badge.officialCredential.credentialName}</strong>
                      <span>Open Badges 3.0 공식 Life Stamp로 승격됨</span>
                      <button className="secondary" onClick={()=>onNavigate("/credentials")}>Life Stamp 보기</button>
                    </div>
                  ) : badge.certificationStatus==="REQUESTED" ? (
                    <div className="activity-badge-requested">
                      <span>본사 심사 중입니다.</span>
                      {badge.certificationRequest&&<button disabled={busy!==""} onClick={()=>cancelCertification(badge.certificationRequest!.requestId)}>요청 취소</button>}
                    </div>
                  ) : badge.certificationStatus==="APPROVED_ISSUANCE_PENDING" ? (
                    <div className="activity-badge-requested"><span>본사 승인 완료 · 공식 Credential 발급 처리 중</span></div>
                  ) : (
                    <div className="activity-badge-certify">
                      <textarea
                        maxLength={1600}
                        value={requestNotes[badge.badgeId]||""}
                        onChange={(e)=>setRequestNotes((current)=>({...current,[badge.badgeId]:e.target.value}))}
                        placeholder="본사 인증 요청 메모 (선택)"
                      />
                      <button className="secondary" disabled={busy!==""||badge.sourceCount<1} onClick={()=>requestCertification(badge)}>본사 인증 요청</button>
                      {badge.sourceCount<1&&<small>인증 요청 전 활동 또는 증빙을 1개 이상 연결해야 합니다.</small>}
                    </div>
                  )}
                </div>
              </article>
            ))}
            {!data.badges.length&&<div className="case-empty">아직 만든 활동배지가 없습니다.</div>}
          </div>
        </section>
      </div>

      <div className="activity-badge-warning">
        <strong>표준 표시는 엄격히 구분합니다.</strong>
        <span>학생 활동배지는 자기 활동 기록입니다. 본사 심사와 공식 발급 절차를 완료한 경우에만 Open Badges 3.0 Life Stamp로 표시합니다.</span>
      </div>
    </section>
  );
}

function SourceSelector({
  title,prefix,items,selected,onToggle
}:{
  title:string;
  prefix:string;
  items:SourceItem[];
  selected:Record<string,boolean>;
  onToggle:(key:string)=>void;
}){
  const [open,setOpen]=useState(false);
  const count=items.filter((item)=>selected[prefix+item.id]).length;
  return (
    <div className="activity-source-group">
      <button type="button" className="activity-source-toggle" onClick={()=>setOpen((v)=>!v)}>
        <strong>{title}</strong><span>{count}/{items.length} 선택</span>
      </button>
      {open&&<div className="activity-source-list">
        {items.map((item)=>(
          <label key={item.id}>
            <input type="checkbox" checked={Boolean(selected[prefix+item.id])} onChange={()=>onToggle(prefix+item.id)} />
            <div><strong>{item.title}</strong><span>{item.subtitle||item.status}</span></div>
          </label>
        ))}
        {!items.length&&<p>연결할 기록이 없습니다.</p>}
      </div>}
    </div>
  );
}
